import { create } from "zustand";
import {
  fetchReviewComments,
  createReviewComment,
  replyToReviewComment,
  resolveReviewThread,
  reopenReviewThread,
} from "../apiServices/reviewCommentsApi";
import type {
  ReviewComment,
  ReviewCommentThread,
  CreateReviewCommentDto,
  ReplyReviewCommentDto,
} from "../models/ReviewComment";
import { buildReviewCommentThreads, isRootComment } from "../models/ReviewComment";

type ReviewCommentStoreState = {
  oem: string | null;
  masterAssemblyId: number | null;

  comments: ReviewComment[];
  selectedThreadId: string | null;

  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
};

type ReviewCommentStoreActions = {
  setContext: (oem: string, masterAssemblyId: number) => void;

  loadComments: () => Promise<void>;

  addRootComment: (payload: CreateReviewCommentDto) => Promise<void>;

  addReply: (payload: ReplyReviewCommentDto) => Promise<void>;

  resolveThread: (threadId: string) => Promise<void>;

  reopenThread: (threadId: string) => Promise<void>;

  selectThread: (threadId: string | null) => void;

  clearComments: () => void;

  getThreads: () => ReviewCommentThread[];

  getThreadById: (threadId: string) => ReviewCommentThread | null;

  getCommentsForManual: (manualId: number) => ReviewComment[];

  getCommentsForBlock: (manualId: number, blockId: string) => ReviewComment[];

  getCommentsForFigure: (figId: number) => ReviewComment[];

  getCommentsForCatalogItem: (figId: number, itemNumber: number) => ReviewComment[];

  getOpenThreadCountForBlock: (manualId: number, blockId: string) => number;

  getOpenThreadCountForFigure: (figId: number) => number;

  getOpenThreadCountForCatalogItem: (figId: number, itemNumber: number) => number;
};

type ReviewCommentStore = ReviewCommentStoreState & ReviewCommentStoreActions;

function replaceRootCommentInList(
  comments: ReviewComment[],
  updatedRoot: ReviewComment
): ReviewComment[] {
  return comments.map((comment) =>
    comment.commentId === updatedRoot.commentId ? updatedRoot : comment
  );
}

export const useReviewCommentStore = create<ReviewCommentStore>((set, get) => ({
  oem: null,
  masterAssemblyId: null,

  comments: [],
  selectedThreadId: null,

  isLoading: false,
  isSaving: false,
  error: null,

  setContext: (oem, masterAssemblyId) =>
    set({
      oem,
      masterAssemblyId,
    }),

  loadComments: async () => {
    const { oem, masterAssemblyId } = get();
    if (!oem || !masterAssemblyId) return;

    set({ isLoading: true, error: null });

    try {
      const comments = await fetchReviewComments(oem, masterAssemblyId);

      set({
        comments,
        isLoading: false,
      });
    } catch (err: any) {
      set({
        error: err.message || "Failed to load review comments",
        isLoading: false,
      });
    }
  },

  addRootComment: async (payload) => {
    const { oem } = get();
    if (!oem) return;

    set({ isSaving: true, error: null });

    try {
      const created = await createReviewComment(oem, payload);

      set((state) => ({
        comments: [...state.comments, created].sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        ),
        selectedThreadId: created.threadId,
        isSaving: false,
      }));
    } catch (err: any) {
      set({
        error: err.message || "Failed to create review comment",
        isSaving: false,
      });
    }
  },

  addReply: async (payload) => {
    const { oem } = get();
    if (!oem) return;

    set({ isSaving: true, error: null });

    try {
      const created = await replyToReviewComment(oem, payload);

      set((state) => ({
        comments: [...state.comments, created].sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        ),
        selectedThreadId: created.threadId,
        isSaving: false,
      }));
    } catch (err: any) {
      set({
        error: err.message || "Failed to reply to review comment",
        isSaving: false,
      });
    }
  },

  resolveThread: async (threadId) => {
    const { oem } = get();
    if (!oem) return;

    set({ isSaving: true, error: null });

    try {
      const updatedRoot = await resolveReviewThread(oem, threadId);

      set((state) => ({
        comments: replaceRootCommentInList(state.comments, updatedRoot),
        isSaving: false,
      }));
    } catch (err: any) {
      set({
        error: err.message || "Failed to resolve review thread",
        isSaving: false,
      });
    }
  },

  reopenThread: async (threadId) => {
    const { oem } = get();
    if (!oem) return;

    set({ isSaving: true, error: null });

    try {
      const updatedRoot = await reopenReviewThread(oem, threadId);

      set((state) => ({
        comments: replaceRootCommentInList(state.comments, updatedRoot),
        isSaving: false,
      }));
    } catch (err: any) {
      set({
        error: err.message || "Failed to reopen review thread",
        isSaving: false,
      });
    }
  },

  selectThread: (threadId) => {
    set({ selectedThreadId: threadId });
  },

  clearComments: () => {
    set({
      comments: [],
      selectedThreadId: null,
      error: null,
    });
  },

  getThreads: () => {
    const { comments } = get();
    return buildReviewCommentThreads(comments);
  },

  getThreadById: (threadId) => {
    const { comments } = get();
    const threads = buildReviewCommentThreads(comments);
    return threads.find((thread) => thread.threadId === threadId) ?? null;
  },

  getCommentsForManual: (manualId) => {
    const { comments } = get();
    return comments.filter((comment) => comment.manualId === manualId);
  },

  getCommentsForBlock: (manualId, blockId) => {
    const { comments } = get();
    return comments.filter(
      (comment) =>
        comment.manualId === manualId &&
        comment.blockId === blockId
    );
  },

  getCommentsForFigure: (figId) => {
    const { comments } = get();
    return comments.filter((comment) => comment.figId === figId);
  },

  getCommentsForCatalogItem: (figId, itemNumber) => {
    const { comments } = get();
    return comments.filter(
      (comment) =>
        comment.figId === figId &&
        comment.itemNumber === itemNumber
    );
  },

  getOpenThreadCountForBlock: (manualId, blockId) => {
    const { comments } = get();
    const roots = comments.filter(
      (comment) =>
        isRootComment(comment) &&
        comment.manualId === manualId &&
        comment.blockId === blockId &&
        comment.status === "open"
    );
    return roots.length;
  },

  getOpenThreadCountForFigure: (figId) => {
    const { comments } = get();
    const roots = comments.filter(
      (comment) =>
        isRootComment(comment) &&
        comment.figId === figId &&
        comment.status === "open"
    );
    return roots.length;
  },

  getOpenThreadCountForCatalogItem: (figId, itemNumber) => {
    const { comments } = get();
    const roots = comments.filter(
      (comment) =>
        isRootComment(comment) &&
        comment.figId === figId &&
        comment.itemNumber === itemNumber &&
        comment.status === "open"
    );
    return roots.length;
  },
}));